import { useSyncExternalStore } from 'react'
import type { BoardKind } from './layout'

/**
 * Выбранный вид поля.
 *
 * Хранится там же, где тема, и по той же причине: это настройка вида, а не
 * часть партии. Играют по одним правилам, а рисуется у каждого своё — сосед
 * за тем же столом может смотреть на крест, пока вы смотрите на меандр.
 *
 * По умолчанию: на телефоне меандр, на большом экране классика. Выбор
 * человека сильнее умолчания — сменивший вид сам получает свой вид всегда.
 *
 * Почему телефону меандр. Крест — 25 клеток в ширину, и на экране 390
 * пикселей клетка выходит около 14, фишка — 11: жалобы «плохо видно» были
 * ровно про это. Меандр — то же кольцо, сложенное в 17×17, клетка в нём
 * около 20 пикселей. Ширину телефону не добавить, а клетку — можно.
 *
 * Внешним хранилищем, а не useState: значение нужно и лобби (там его
 * переключают), и экрану партии (там от него зависит запрос доски), а копия
 * в каждом хуке разъехалась бы при переключении на ходу. Контекста для этого
 * не хватило бы одного — провайдер пришлось бы поднимать над всем App
 * ради одной строки.
 */
const KEY = 'ludo.board'

function stored(): BoardKind {
  try {
    const saved = localStorage.getItem(KEY)

    if (saved === 'meander' || saved === 'classic') return saved
  } catch {
    // Приватный режим: вид просто не запомнится.
  }

  return phone() ? 'meander' : 'classic'
}

/**
 * Узкий экран — тот же порог, по которому в стилях расходятся раскладки
 * (60rem): на нём доска упирается в ширину экрана.
 */
function phone(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 60rem)').matches
}

let current: BoardKind = stored()
const listeners = new Set<() => void>()

function subscribe(listener: () => void): () => void {
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}

export function setBoardKind(next: BoardKind): void {
  if (next === current) return

  current = next

  try {
    localStorage.setItem(KEY, next)
  } catch {
    // см. stored()
  }

  for (const listener of listeners) listener()
}

export function useBoardKind(): BoardKind {
  /*
   * Третий аргумент — значение для сервера: рендер на сервере проекта нет,
   * но без него useSyncExternalStore падает в тестовой среде, где React
   * проверяет гидратацию.
   */
  return useSyncExternalStore(
    subscribe,
    () => current,
    () => current,
  )
}
